import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { restGet, restRpc } from '@/lib/supabase';
import './AdminOrders.css';

type OrderStatus = 'payment_pending' | 'paid' | 'payment_failed' | 'cancelled';
type FulfillmentStatus = 'received' | 'processing' | 'packing' | 'shipped' | 'delivered';
type Range = 'today' | '7d' | '30d' | 'all';
type OrderRow = {
  id: string;
  order_number: string;
  user_id: string | null;
  status: OrderStatus;
  subtotal: number;
  shipping_amount: number;
  total: number;
  currency: string;
  customer: { firstName?: string; lastName?: string; phone?: string; email?: string } | null;
  shipping: { fulfillment?: string; street?: string; city?: string; zip?: string } | null;
  items: Array<{ id?: number; name?: string; sku?: string; qty?: number; unitPrice?: number; lineTotal?: number; options?: Record<string, string> }> | null;
  payment_transaction_id: string | null;
  payment_approval_code: string | null;
  payment_ccode: string | null;
  payment_method: string | null;
  fulfillment_status: FulfillmentStatus;
  tracking_carrier: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  fulfillment_updated_at: string | null;
  admin_seen_at: string | null;
  created_at: string;
  paid_at: string | null;
};

type Draft={status:FulfillmentStatus;carrier:string;number:string;url:string};

const money = (value: number) => new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: 0 }).format(Number(value || 0));
const dateTime = (value: string) => new Intl.DateTimeFormat('he-IL', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
const labels: Record<OrderStatus, string> = { paid: 'שולם', payment_pending: 'ממתין לתשלום', payment_failed: 'נכשל', cancelled: 'בוטל' };
const fulfillmentLabels:Record<FulfillmentStatus,string>={received:'התקבלה',processing:'בטיפול',packing:'בהכנה',shipped:'נשלחה',delivered:'נמסרה'};

const rangeStart = (range: Range) => {
  const now = new Date();
  if (range === 'all') return null;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (range === '7d') start.setDate(start.getDate() - 6);
  if (range === '30d') start.setDate(start.getDate() - 29);
  return start;
};

const csvCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
const makeDraft=(order:OrderRow):Draft=>({status:order.fulfillment_status||'received',carrier:order.tracking_carrier||'',number:order.tracking_number||'',url:order.tracking_url||''});

export function AdminOrders() {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'all' | OrderStatus>('all');
  const [fulfillment, setFulfillment] = useState<'all'|FulfillmentStatus>('all');
  const [range, setRange] = useState<Range>('30d');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [drafts,setDrafts]=useState<Record<string,Draft>>({});
  const [savingId,setSavingId]=useState<string|null>(null);
  const [saveMessage,setSaveMessage]=useState<Record<string,string>>({});

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const rows = await restGet<OrderRow[]>('mielley_orders', 'select=*&order=created_at.desc&limit=500');
      setOrders(rows);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'לא ניתן לטעון הזמנות.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load(true);
    }, 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  const rangedOrders = useMemo(() => {
    const start = rangeStart(range);
    return start ? orders.filter(order => new Date(order.created_at) >= start) : orders;
  }, [orders, range]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rangedOrders.filter(order => {
      if (status !== 'all' && order.status !== status) return false;
      if(fulfillment!=='all'&&order.fulfillment_status!==fulfillment)return false;
      if (!q) return true;
      const customer = `${order.customer?.firstName || ''} ${order.customer?.lastName || ''} ${order.customer?.phone || ''} ${order.customer?.email || ''}`.toLowerCase();
      return order.order_number.toLowerCase().includes(q) || customer.includes(q) || (order.payment_transaction_id || '').toLowerCase().includes(q) || (order.tracking_number||'').toLowerCase().includes(q);
    });
  }, [rangedOrders, query, status, fulfillment]);

  const kpis = useMemo(() => {
    const paid = rangedOrders.filter(x => x.status === 'paid');
    const pending = rangedOrders.filter(x => x.status === 'payment_pending');
    const failed = rangedOrders.filter(x => x.status === 'payment_failed');
    const unseen = rangedOrders.filter(x => x.status==='paid' && !x.admin_seen_at);
    const active = paid.filter(x => x.fulfillment_status!=='delivered');
    const revenue = paid.reduce((sum, x) => sum + Number(x.total || 0), 0);
    return {orders:rangedOrders.length,paid:paid.length,revenue,unseen:unseen.length,active:active.length,pending:pending.length,failed:failed.length};
  }, [rangedOrders]);

  const exportCsv = () => {
    const header = ['Order','Created','Customer','Email','Phone','Items','Total','Payment status','Fulfillment','Transaction','Tracking'];
    const lines = filtered.map(order => [order.order_number,order.created_at,`${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim(),order.customer?.email || '',order.customer?.phone || '',order.items?.reduce((sum, item) => sum + Number(item.qty || 0), 0) || 0,Number(order.total || 0),labels[order.status],fulfillmentLabels[order.fulfillment_status||'received'],order.payment_transaction_id || '',order.tracking_number||''].map(csvCell).join(','));
    const blob = new Blob(['\uFEFF' + [header.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const href = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = href; a.download = `mielley-orders-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(href);
  };

  const toggleDetails=async(order:OrderRow)=>{
    const willOpen=expanded!==order.id;
    setExpanded(willOpen?order.id:null);
    if(!willOpen)return;
    setDrafts(prev=>({...prev,[order.id]:prev[order.id]||makeDraft(order)}));
    if(!order.admin_seen_at){
      try{
        await restRpc('mielley_admin_mark_order_seen',{p_order_id:order.id});
        const seenAt=new Date().toISOString();
        setOrders(prev=>prev.map(row=>row.id===order.id?{...row,admin_seen_at:seenAt}:row));
      }catch{}
    }
  };

  const updateDraft=(id:string,patch:Partial<Draft>)=>setDrafts(prev=>({...prev,[id]:{...(prev[id]||{status:'received',carrier:'',number:'',url:''}),...patch}}));

  const saveFulfillment=async(order:OrderRow)=>{
    if(order.status!=='paid')return;
    const draft=drafts[order.id]||makeDraft(order);
    setSavingId(order.id); setSaveMessage(prev=>({...prev,[order.id]:''}));
    try{
      await restRpc('mielley_admin_update_fulfillment',{
        p_order_id:order.id,
        p_status:draft.status,
        p_tracking_carrier:draft.carrier.trim()||null,
        p_tracking_number:draft.number.trim()||null,
        p_tracking_url:draft.url.trim()||null,
      });
      setSaveMessage(prev=>({...prev,[order.id]:'נשמר בהצלחה ✓'}));
      await load(true);
    }catch(err:any){
      setSaveMessage(prev=>({...prev,[order.id]:`שגיאה: ${String(err?.message||'לא נשמר')}`}));
    }finally{setSavingId(null)}
  };

  return <div className="mielley-orders-admin" dir="rtl">
    <section className="orders-head">
      <div><span>PAYMENTS & ORDERS</span><h2>מרכז ההזמנות והתשלומים</h2><p>תשלום ננעל לפי HYP/MAX; מכאן מנהלים את הטיפול, האריזה, המשלוח והמסירה.</p></div>
      <div className="orders-head-actions"><span className="orders-live"><i />LIVE</span><button className="orders-secondary" onClick={exportCsv} disabled={!filtered.length}>⇩ ייצוא CSV</button><button onClick={() => void load()} disabled={loading}>↻ רענון</button></div>
    </section>

    <section className="orders-kpis v4">
      <article><span>סה״כ הזמנות</span><b>{kpis.orders}</b><small>{range === 'all' ? 'כל התקופה' : range === 'today' ? 'היום' : range === '7d' ? '7 ימים' : '30 ימים'}</small></article>
      <article className="paid"><span>מחזור ששולם</span><b>{money(kpis.revenue)}</b><small>{kpis.paid} עסקאות מאושרות</small></article>
      <article className={kpis.unseen?'new':''}><span>הזמנות חדשות</span><b>{kpis.unseen}</b><small>טרם נפתחו באדמין</small></article>
      <article><span>בטיפול</span><b>{kpis.active}</b><small>שולמו וטרם נמסרו</small></article>
      <article className={kpis.pending ? 'warning' : ''}><span>ממתינות לתשלום</span><b>{kpis.pending}</b><small>טרם התקבל אישור סופי</small></article>
      <article className={kpis.failed ? 'danger' : ''}><span>נכשלו</span><b>{kpis.failed}</b><small>כדאי לבדוק ולחזור ללקוחה</small></article>
    </section>

    <section className="orders-control-panel v4">
      <div className="orders-range-tabs">{([['today','היום'],['7d','7 ימים'],['30d','30 ימים'],['all','הכל']] as Array<[Range,string]>).map(([value,label]) => <button key={value} className={range === value ? 'active' : ''} onClick={() => setRange(value)}>{label}</button>)}</div>
      <div className="orders-toolbar v4"><input value={query} onChange={e => setQuery(e.target.value)} placeholder="חיפוש לפי הזמנה, לקוחה, טלפון, מעקב או מזהה עסקה"/><select value={status} onChange={e => setStatus(e.target.value as 'all' | OrderStatus)}><option value="all">כל סטטוסי התשלום</option><option value="paid">שולם</option><option value="payment_pending">ממתין</option><option value="payment_failed">נכשל</option><option value="cancelled">בוטל</option></select><select value={fulfillment} onChange={e=>setFulfillment(e.target.value as 'all'|FulfillmentStatus)}><option value="all">כל מצבי הטיפול</option>{Object.entries(fulfillmentLabels).map(([value,label])=><option value={value} key={value}>{label}</option>)}</select></div>
      <div className="orders-meta"><span>{filtered.length} תוצאות</span>{lastRefresh && <span>עודכן {new Intl.DateTimeFormat('he-IL', { hour: '2-digit', minute: '2-digit' }).format(lastRefresh)}</span>}</div>
    </section>

    {error && <div className="orders-error">שגיאה בטעינת ההזמנות: {error}</div>}

    <div className="orders-table-wrap"><table className="orders-table v4"><thead><tr><th>הזמנה</th><th>תאריך</th><th>לקוחה</th><th>פריטים</th><th>סכום</th><th>תשלום</th><th>טיפול</th><th>עסקת HYP</th></tr></thead><tbody>
      {loading && <tr><td colSpan={8} className="orders-empty">טוען הזמנות…</td></tr>}
      {!loading && !filtered.length && <tr><td colSpan={8} className="orders-empty">אין הזמנות להצגה בטווח שנבחר.</td></tr>}
      {!loading && filtered.map(order => <Fragment key={order.id}>
        <tr
          className={`order-summary-row ${order.status==='paid'&&!order.admin_seen_at?'unseen-order':''} ${expanded===order.id?'is-expanded':''}`}
          onClick={() => void toggleDetails(order)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void toggleDetails(order);
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={expanded===order.id}
          title={expanded===order.id?'סגירת פרטי ההזמנה':'לחץ לפתיחת פרטי ההזמנה'}
        >
          <td><strong>{order.order_number}</strong>{order.status==='paid'&&!order.admin_seen_at&&<em className="new-order-badge">חדש</em>}</td>
          <td>{dateTime(order.created_at)}</td>
          <td><b>{order.customer?.firstName || '—'} {order.customer?.lastName || ''}</b><small>{order.customer?.phone || order.customer?.email || '—'}</small></td>
          <td>{order.items?.reduce((sum, x) => sum + Number(x.qty || 0), 0) || 0}</td>
          <td><strong>{money(order.total)}</strong></td>
          <td><span className={`order-status ${order.status}`}>{labels[order.status]}</span></td>
          <td><span className={`fulfillment-status ${order.fulfillment_status||'received'}`}>{fulfillmentLabels[order.fulfillment_status||'received']}</span></td>
          <td><small>{order.payment_transaction_id || '—'}</small></td>
        </tr>
        {expanded === order.id && <tr className="order-detail-row"><td colSpan={8}><div className="order-detail-card v4">
          <section><span>לקוחה</span><b>{order.customer?.firstName} {order.customer?.lastName}</b><small>{order.customer?.email}</small><small>{order.customer?.phone}</small>{order.user_id&&<small>מחובר לחשבון לקוחה ✓</small>}</section>
          <section><span>אספקה</span><b>{order.shipping?.fulfillment === 'pickup' ? 'איסוף עצמי' : 'משלוח'}</b><small>{[order.shipping?.street, order.shipping?.city, order.shipping?.zip].filter(Boolean).join(', ') || '—'}</small></section>
          <section><span>תשלום — נעול</span><b>{labels[order.status]}</b><small>עסקה: {order.payment_transaction_id || '—'}</small><small>אישור: {order.payment_approval_code || '—'} · CCode: {order.payment_ccode || '—'}</small></section>
          <section className="detail-total"><span>סה״כ</span><b>{money(order.total)}</b><small>{order.paid_at ? `שולם ${dateTime(order.paid_at)}` : 'טרם התקבל אישור תשלום'}</small></section>

          <section className="fulfillment-editor">
            <div className="fulfillment-editor-head"><div><span>ORDER FLOW</span><b>סטטוס טיפול ומשלוח</b></div><em className={`fulfillment-status ${order.fulfillment_status||'received'}`}>{fulfillmentLabels[order.fulfillment_status||'received']}</em></div>
            {order.status!=='paid'?<div className="fulfillment-lock">ניתן לעדכן טיפול רק אחרי שהתשלום מאומת כ־״שולם״ על ידי HYP/MAX.</div>:<>
              <div className="fulfillment-form-grid">
                <label><span>מצב ההזמנה</span><select value={(drafts[order.id]||makeDraft(order)).status} onChange={e=>updateDraft(order.id,{status:e.target.value as FulfillmentStatus})}>{Object.entries(fulfillmentLabels).map(([value,label])=><option key={value} value={value}>{label}</option>)}</select></label>
                <label><span>חברת משלוחים</span><input value={(drafts[order.id]||makeDraft(order)).carrier} onChange={e=>updateDraft(order.id,{carrier:e.target.value})} placeholder="לדוגמה: HFD / צ׳יטה / שליח פרטי"/></label>
                <label><span>מספר מעקב</span><input value={(drafts[order.id]||makeDraft(order)).number} onChange={e=>updateDraft(order.id,{number:e.target.value})} placeholder="מספר מעקב"/></label>
                <label className="full"><span>קישור מעקב</span><input value={(drafts[order.id]||makeDraft(order)).url} onChange={e=>updateDraft(order.id,{url:e.target.value})} placeholder="https://..."/></label>
              </div>
              <div className="fulfillment-save-row"><small>{saveMessage[order.id]||'השינוי יוצג מיד גם באזור האישי של הלקוחה.'}</small><button disabled={savingId===order.id} onClick={()=>void saveFulfillment(order)}>{savingId===order.id?'שומר…':'שמירת סטטוס'}</button></div>
            </>}
          </section>

          <div className="order-items-detail">{order.items?.map((item, index) => <div key={`${order.id}-${index}`}><b>{item.name || `מוצר ${item.id || ''}`}</b><span>{item.qty || 0} × {money(Number(item.unitPrice || 0))}</span><small>{item.sku || ''}</small></div>)}</div>
        </div></td></tr>}
      </Fragment>)}
    </tbody></table></div>
  </div>;
}
